const KEYS = {
    individual: {
        token: 'individual_auth_token',
        tokenType: 'individual_token_type',
        expiresAt: 'individual_token_expires_at',
        user: 'individual_user',
    },
    corporate: {
        token: 'corporate_auth_token',
        tokenType: 'corporate_token_type',
        expiresAt: 'corporate_token_expires_at',
        user: 'corporate_user',
    },
    admin: {
        token: 'admin_auth_token',
        tokenType: 'admin_token_type',
        expiresAt: 'admin_token_expires_at',
        user: 'admin_user',
    },
} as const;

type UserType = 'individual' | 'corporate' | 'admin';

function isTokenValid(type: UserType): boolean {
    const expiresAt = localStorage.getItem(KEYS[type].expiresAt);
    if (!expiresAt) return false;
    const expiry = new Date(expiresAt);
    return !isNaN(expiry.getTime()) && expiry > new Date();
}

function clearAuth(type: UserType): void {
    const k = KEYS[type];
    localStorage.removeItem(k.token);
    localStorage.removeItem(k.tokenType);
    localStorage.removeItem(k.expiresAt);
    localStorage.removeItem(k.user);
}

function getToken(type: UserType): string | null {
    const token = localStorage.getItem(KEYS[type].token);
    if (!token) return null;
    if (!isTokenValid(type)) {
        clearAuth(type);
        return null;
    }
    return token;
}

export function getIndividualToken(): string | null {
    return getToken('individual');
}
export function getCorporateToken(): string | null {
    return getToken('corporate');
}
export function getAdminToken(): string | null {
    return getToken('admin');
}

export function setIndividualAuth(
    token: string,
    tokenType: string,
    expiresAt: string,
    user: { user_id: string; user_name: string; user_email: string },
): void {
    localStorage.setItem(KEYS.individual.token, token);
    localStorage.setItem(KEYS.individual.tokenType, tokenType);
    localStorage.setItem(KEYS.individual.expiresAt, expiresAt);
    localStorage.setItem(KEYS.individual.user, JSON.stringify(user));
}

export function setCorporateAuth(token: string, tokenType: string, expiresAt: string, user: object): void {
    localStorage.setItem(KEYS.corporate.token, token);
    localStorage.setItem(KEYS.corporate.tokenType, tokenType);
    localStorage.setItem(KEYS.corporate.expiresAt, expiresAt);
    localStorage.setItem(KEYS.corporate.user, JSON.stringify(user));
}

export function clearAllAuth(): void {
    clearAuth('individual');
    clearAuth('corporate');
    clearAuth('admin');
}

export function getActiveSession(): { email: string; userType: 'individual' | 'corporate' } | null {
    // Corporate takes priority (matches API interceptor order)
    const corpToken = getCorporateToken();
    if (corpToken) {
        try {
            const user = JSON.parse(localStorage.getItem(KEYS.corporate.user) ?? '{}');
            const email = user.email || user.user_email || '';
            if (email) return { email, userType: 'corporate' };
        } catch {}
    }

    const indToken = getIndividualToken();
    if (indToken) {
        try {
            const user = JSON.parse(localStorage.getItem(KEYS.individual.user) ?? '{}');
            const email = user.user_email || '';
            if (email) return { email, userType: 'individual' };
        } catch {}
    }

    return null;
}
